<template>
  <div>
    <h1 class="modal-h1 mb-3">
      <button type="button"
              class="btn btn-back"
              @click="$emit('cancel')"
      />
      <span v-if="emailTemplate.type_id === 1">
        Claim templates
      </span>
      <span v-else-if="emailTemplate.type_id === 2">
        Acknowledgement template
      </span>
      <span v-else-if="emailTemplate.type_id === 3">
        Settlement template
      </span>
    </h1>
    <div class="l-bg-white" v-if="loaded">
      <div class="row mb-2">
        <div class="col">
          <div class="select-wrap">
            <label>Claim type</label>
            <key-multiselect v-model="template.claim_type_ids"
                             :options="claimTypes"
                             :group-select="true"
                             select-group-label="Select All"
                             deselect-group-label="Deselect All"
                             :multiple="true"
                             :clear-on-select="false"
                             :hide-selected="true"
                             :preserve-search="false"
                             label="name"
                             track-by="id"
                             @input="updateClaimTypes"
            >
              <template slot="tag" slot-scope="props">
                <span class="multiselect__tag">
                  <span>{{ props.option.name }}</span>
                  <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                </span>
              </template>
            </key-multiselect>
          </div>
          <error-field :errors="errors" field="claim_type_ids" />
        </div>
        <div class="col" v-if="emailTemplate.type_id !== 1">
          <div class="select-wrap h-auto">
            <label>Companies</label>
            <key-multiselect v-model="template.modelable_ids"
                             v-if="companies.length"
                             :options="companies"
                             :multiple="true"
                             :clear-on-select="false"
                             :hide-selected="true"
                             :preserve-search="false"
                             label="name"
                             track-by="id"
            >
              <template slot="tag" slot-scope="props">
                <span class="multiselect__tag">
                  <span>{{ props.option.name }}</span>
                  <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                </span>
              </template>
            </key-multiselect>
          </div>
          <error-field :errors="errors" field="modelable_ids" />
        </div>
      </div>
      <div class="row mb-2">
        <div class="col">
          <div class="select-wrap">
            <label>Template name</label>
            <input v-model="template.name" class="form-control" type="text" @keydown="clearError($event)" />
          </div>
          <error-field :errors="errors" field="name" />
        </div>
        <div class="col">
          <div class="select-wrap">
            <label>Email subject</label>
            <input v-model="template.subject" class="form-control" @keydown="clearError($event)" ref="subject"
                   @click="addingVariableOn = 'subject'"
            />
          </div>
          <error-field :errors="errors" field="subject" />
        </div>
      </div>
      <div class="row mb-2">
        <div class="col">
          <div class="select-wrap h-auto">
            <label>Email body</label>
            <textarea v-model="template.body" class="select-wrap-textarea form-control" cols="30" rows="10" ref="ta"
                      @click="addingVariableOn = 'ta'"
                      @keydown="clearError($event)"
            />
          </div>
        </div>
        <error-field :errors="errors" field="body" />
      </div>
      <div class="row mb-4">
        <div class="col">
          <span class="badge badge-primary mx-1 unselectable pointer"
                v-for="(variable, key, index) in variables" :key="index"
                @click="insertVariable(variable)"
          >{{ key }}</span>
        </div>
      </div>
      <div class="row">
        <div class="col-12 text-right">
          <button type="button" class="btn btn-secondary" @click="closeModal()">
            Cancel
          </button>
          <button type="button" class="btn btn-primary ld-ext-right"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  @click="save"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <div class="clearfix" />
    </div>
    <template v-else>
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import toastr from 'toastr';
import Errors from '../../classes/Errors';
import ErrorField from '../ErrorField';

export default {
  components: {
    ErrorField,
  },
  props: {
    emailTemplate: {
      type: Object,
      default() {
        return '';
      },
    },
  },
  data: () => ({
    addingVariableOn: 'subject',
    buttonDisabled: false,
    claimTypes: [],
    errors: new Errors(),
    filteredClaimTypes: [],
    loaded: false,
    loadingButton: false,
    showCreateSidebar: false,
    template: {
      body: '',
      claim_type_ids: [],
      claim_types: [],
      modelable_ids: [],
      name: '',
      subject: '',
    },
    templates: null,
    variables: [],
  }),
  computed: {
    ...mapState({
      companies: state => state.companies.companies.data,
    }),
  },
  mounted() {
    this.getVariables();
    this.getClaimTypes();
  },
  methods: {
    getClaimTypes() {
      axios.get('/claims/types').then(res => {
        this.claimTypes = res.data.data;
        this.loaded = true;
      });
    },
    insertVariable(variable) {
      const textarea = this.addingVariableOn === 'subject' ? this.$refs.subject : this.$refs.ta;
      const cursorPosition = textarea.selectionStart;
      const textBefore = textarea.value.substring(0, cursorPosition);
      const textAfter = textarea.value.substring(cursorPosition, textarea.value.length);
      textarea.value = textBefore + variable + textAfter;
      if (this.addingVariableOn === 'subject') {
        this.template.subject = textarea.value;
      } else {
        this.template.body = textarea.value;
      }
      if (textarea.setSelectionRange) {
        textarea.focus();
        textarea.setSelectionRange(
          cursorPosition + variable.length,
          cursorPosition + variable.length,
        );
      }
    },
    getEmailTemplates() {
      axios
        .get(`claims/templates?type_id=${this.emailTemplate.type_id}`)
        .then(res => {
          this.templates = res.data.data;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    getVariables() {
      axios
        .get('claims/templates/variables')
        .then(res => {
          this.variables = res.data;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    clearError(event) {
      this.errors.clear(event.target.name);
    },
    closeModal() {
      this.$emit('cancel');
    },
    save() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .post('claims/templates', {
          type_id: this.emailTemplate.type_id,
          subject: this.template.subject,
          name: this.template.name,
          model_ids: this.template.model_ids,
          claim_types: this.template.claim_types,
          claim_type_ids: this.template.claim_type_ids,
          body: this.template.body,
        })
        .then(() => {
          toastr.success('Template saved');
          this.$emit('cancel');
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    updateClaimTypes(types) {
      const selectedTypes = [];
      types.forEach(type => {
        selectedTypes.push(type);
      });
      this.$set(this.template, 'claim_type_ids', selectedTypes);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/settings/CreateEmailTemplate.vue